import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Download } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import api from "../lib/api";
import {
  TITLES,
  PROVINCES,
  GENDERS,
  STATUS_IN_COUNTRY,
  PROGRAMS,
  CAMPUSES,
  MONTHS,
} from "../lib/data";
import { toast } from "sonner";

const Field = ({ label, required, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="text-sm font-medium text-slate-700">
      {label} {required && <span className="text-[#C49A3A]">*</span>}
    </span>
    <div className="mt-1.5">{children}</div>
  </label>
);

const Input = (props) => (
  <input
    {...props}
    className={
      "w-full rounded-md border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder-slate-400 input-focus " +
      (props.className || "")
    }
  />
);

const Select = ({ children, ...props }) => (
  <select
    {...props}
    className={
      "w-full rounded-md border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 input-focus " +
      (props.className || "")
    }
  >
    {children}
  </select>
);

const Section = ({ number, title, children }) => (
  <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 sm:p-8">
    <div className="flex items-center gap-3 mb-6">
      <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-gold">
        Section {number}
      </div>
      <div className="h-px flex-1 bg-slate-200" />
    </div>
    <h2 className="font-display text-2xl font-semibold text-cct-blue mb-6">
      {title}
    </h2>
    {children}
  </div>
);

const Check = ({ checked, onChange, children, required }) => (
  <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-0.5 w-4 h-4 rounded border-slate-300 accent-[#1E3A8A]"
    />
    <span>
      {children} {required && <span className="text-[#C49A3A]">*</span>}
    </span>
  </label>
);

const initial = {
  counsellor: "",
  agency: "",
  title: "",
  first_name: "",
  last_name: "",
  street: "",
  city: "",
  province: "",
  country: "Canada",
  postal_code: "",
  email: "",
  phone: "",
  alt_phone: "",
  gender: "",
  status_in_country: "",
  date_of_birth: "",
  program_title: "",
  campus: "",
  program_hours: "",
  program_weeks: "",
  program_months: "",
  program_years: "",
  start_month: "",
  start_year: "",
  has_photo_id: false,
  agrees_policies: false,
  authorizes_release: false,
  mailing_list: false,
  signed_by: "",
  signed_date: "",
};

export default function ApplyDomestic() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onSubmit = async () => {
    // required minimal validation
    const req = [
      "first_name",
      "last_name",
      "street",
      "city",
      "province",
      "country",
      "postal_code",
      "email",
      "phone",
      "date_of_birth",
      "program_title",
      "campus",
      "signed_by",
      "signed_date",
    ];
    for (const k of req) {
      if (!form[k]) {
        toast.error(`Please fill: ${k.replace(/_/g, " ")}`);
        return;
      }
    }
    if (!form.agrees_policies || !form.authorizes_release) {
      toast.error("Please confirm all required acknowledgements");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/apply/domestic", form);
      setSubmitted(data);
      toast.success("Application submitted successfully");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-cct-ambient">
        <Header minimal />
        <main className="max-w-2xl mx-auto px-4 py-20 text-center fade-up">
          <div className="inline-flex w-16 h-16 rounded-full bg-[#1E3A8A]/10 items-center justify-center">
            <CheckCircle2 className="w-9 h-9 text-cct-blue" />
          </div>
          <h1 className="mt-6 font-display text-4xl font-semibold text-cct-blue">
            Application Received
          </h1>
          <p className="mt-3 text-slate-600">
            Your domestic application has been received. The admissions team will be in touch shortly.
          </p>
          <div className="mt-6 inline-block bg-white border border-slate-200 rounded-lg px-4 py-3 text-sm">
            <span className="text-slate-500">Reference ID:</span>{" "}
            <span className="font-mono text-slate-900">{submitted.id}</span>
          </div>
          <div className="mt-10">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-medium text-cct-blue hover:underline"
            >
              <ArrowLeft className="w-4 h-4" /> Back to portal
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cct-ambient">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 pb-20">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-cct-blue mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Back to portal
        </Link>
        <div className="fade-up">
          <div className="text-[11px] tracking-[0.2em] uppercase font-semibold text-gold">
            For Domestic Students
          </div>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold text-cct-blue">
            Domestic Student Application
          </h1>
          <p className="mt-3 text-slate-600 max-w-2xl">
            Complete the form below. Submit it to CCT Admissions, or download
            / print / email a PDF copy.
          </p>
          <button
            onClick={() => window.print()}
            className="mt-5 inline-flex items-center gap-2 bg-white border border-slate-300 text-slate-700 text-sm font-medium px-4 py-2 rounded-md hover:border-cct-blue hover:text-cct-blue transition-colors"
          >
            <Download className="w-4 h-4" /> Download / Print Blank Form
          </button>
        </div>

        <div className="mt-10 space-y-6">
          <Section number={1} title="Referral Agency">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Counsellor">
                <Input
                  value={form.counsellor}
                  onChange={(e) => update("counsellor", e.target.value)}
                />
              </Field>
              <Field label="Agency">
                <Input
                  value={form.agency}
                  onChange={(e) => update("agency", e.target.value)}
                />
              </Field>
            </div>
          </Section>

          <Section number={2} title="Contact Information">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Title">
                <Select
                  value={form.title}
                  onChange={(e) => update("title", e.target.value)}
                >
                  <option value="">— Select —</option>
                  {TITLES.map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Legal First Name(s)" required>
                <Input
                  value={form.first_name}
                  onChange={(e) => update("first_name", e.target.value)}
                />
              </Field>
              <Field label="Last Name" required>
                <Input
                  value={form.last_name}
                  onChange={(e) => update("last_name", e.target.value)}
                />
              </Field>
              <Field label="Street Number & Name" required>
                <Input
                  value={form.street}
                  onChange={(e) => update("street", e.target.value)}
                />
              </Field>
              <Field label="City" required>
                <Input
                  value={form.city}
                  onChange={(e) => update("city", e.target.value)}
                />
              </Field>
              <Field label="Province" required>
                <Select
                  value={form.province}
                  onChange={(e) => update("province", e.target.value)}
                >
                  <option value="">— Select —</option>
                  {PROVINCES.map((p) => (
                    <option key={p}>{p}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Country" required>
                <Input
                  value={form.country}
                  onChange={(e) => update("country", e.target.value)}
                />
              </Field>
              <Field label="Postal / Zip Code" required>
                <Input
                  value={form.postal_code}
                  onChange={(e) => update("postal_code", e.target.value)}
                />
              </Field>
              <Field label="Email Address" required>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                />
              </Field>
              <Field label="Phone Number" required>
                <Input
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                />
              </Field>
              <Field label="Alt Phone Number">
                <Input
                  value={form.alt_phone}
                  onChange={(e) => update("alt_phone", e.target.value)}
                />
              </Field>
              <Field label="Gender">
                <Select
                  value={form.gender}
                  onChange={(e) => update("gender", e.target.value)}
                >
                  <option value="">— Select —</option>
                  {GENDERS.map((g) => (
                    <option key={g}>{g}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Status in Country">
                <Select
                  value={form.status_in_country}
                  onChange={(e) => update("status_in_country", e.target.value)}
                >
                  <option value="">— Select —</option>
                  {STATUS_IN_COUNTRY.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Date of Birth" required>
                <Input
                  type="date"
                  value={form.date_of_birth}
                  onChange={(e) => update("date_of_birth", e.target.value)}
                />
              </Field>
            </div>
          </Section>

          <Section number={3} title="Program Information">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Program Title" required className="sm:col-span-2">
                <Select
                  value={form.program_title}
                  onChange={(e) => update("program_title", e.target.value)}
                >
                  <option value="">— Select —</option>
                  {PROGRAMS.map((p) => (
                    <option
                      key={`${p.name}-${p.type}`}
                      value={`${p.name} — ${p.type}`}
                    >
                      {p.name} — {p.type}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Campus" required>
                <Select
                  value={form.campus}
                  onChange={(e) => update("campus", e.target.value)}
                >
                  <option value="">— Select —</option>
                  {CAMPUSES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Program Length">
                <div className="grid grid-cols-4 gap-2">
                  <Input
                    placeholder="Hours"
                    value={form.program_hours}
                    onChange={(e) => update("program_hours", e.target.value)}
                  />
                  <Input
                    placeholder="Weeks"
                    value={form.program_weeks}
                    onChange={(e) => update("program_weeks", e.target.value)}
                  />
                  <Input
                    placeholder="Months"
                    value={form.program_months}
                    onChange={(e) => update("program_months", e.target.value)}
                  />
                  <Input
                    placeholder="Years"
                    value={form.program_years}
                    onChange={(e) => update("program_years", e.target.value)}
                  />
                </div>
              </Field>
              <Field label="Start Month">
                <Select
                  value={form.start_month}
                  onChange={(e) => update("start_month", e.target.value)}
                >
                  <option value="">— Select —</option>
                  {MONTHS.map((m) => (
                    <option key={m}>{m}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Start Year">
                <Input
                  type="number"
                  value={form.start_year}
                  onChange={(e) => update("start_year", e.target.value)}
                  placeholder="2025"
                />
              </Field>
            </div>
          </Section>

          <Section number={4} title="Application Checklist & Acknowledgements">
            <div className="space-y-3">
              <Check
                checked={form.has_photo_id}
                onChange={(v) => update("has_photo_id", v)}
              >
                I have enclosed a copy of my valid photo ID.
              </Check>
              <Check
                checked={form.agrees_policies}
                onChange={(v) => update("agrees_policies", v)}
                required
              >
                I confirm that I have received a copy of the program
                information, meet the entrance requirements, and that CCT
                Admissions has provided me with the following policies:{" "}
                <em>
                  Student Withdrawal / Refund, Admission, Prior Learning
                  Assessment / Credit Transfer, Attendance, Student Guidelines
                  for Dispute Resolution, Work Experience: Practicum / Co-Op
                  Work Term, English Language Proficiency.
                </em>
              </Check>
              <Check
                checked={form.authorizes_release}
                onChange={(v) => update("authorizes_release", v)}
                required
              >
                I authorize CCT Admissions to obtain any details relating to
                my admission requirements listed with this application, and to
                release information regarding the status of my application to
                my agent (if applicable).
              </Check>
              <Check
                checked={form.mailing_list}
                onChange={(v) => update("mailing_list", v)}
              >
                Add me to the CCT Admissions mailing list for promotions,
                program information and other services. (Optional)
              </Check>
            </div>
          </Section>

          <Section number={5} title="Applicant Signature">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Signed by (typed full name)" required>
                <Input
                  value={form.signed_by}
                  onChange={(e) => update("signed_by", e.target.value)}
                />
              </Field>
              <Field label="Date" required>
                <Input
                  type="date"
                  value={form.signed_date}
                  onChange={(e) => update("signed_date", e.target.value)}
                />
              </Field>
            </div>
            <div className="mt-8 flex flex-wrap gap-3 justify-end">
              <button
                onClick={() => window.print()}
                className="bg-white border border-slate-300 text-slate-700 text-sm font-medium px-4 py-2.5 rounded-md hover:border-cct-blue hover:text-cct-blue"
              >
                Print
              </button>
              <button
                onClick={onSubmit}
                disabled={submitting}
                className="bg-cct-blue text-white text-sm font-semibold px-5 py-2.5 rounded-md hover:bg-[#1a3380] disabled:opacity-60"
              >
                {submitting ? "Submitting..." : "Submit Application"}
              </button>
            </div>
          </Section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
